import { Component, Element, Event, EventEmitter, Fragment, h, Host, Method, Prop } from "@stencil/core";

import { StzhInputChangeEvent, StzhPaginationChangeEvent } from "../../index";

import { StzhPaginationLocalizedText } from "./stzh-pagination.localization";

/**
 */
@Component({
  tag: "stzh-pagination",
  styleUrl: "stzh-pagination.scss",
  scoped: true,
})
export class StzhPagination {
  /** Total pages available. */
  @Prop() total: number = 1;

  /** Name of the hidden date picker input. */
  @Prop({ reflect: true }) name: string = "";

  /** Current page. */
  @Prop({ mutable: true }) value: number = 1;

  /** Label for dropdown */
  @Prop() label: string = "";

  /** Whether label is hidden. */
  @Prop() labelHidden: boolean = false;

  /** Whether the element is readonly or not */
  @Prop({ reflect: true }) readonly: boolean = false;

  /** Whether the element is disabled or not */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** Whether the current page is mutable via input */
  @Prop({ reflect: true }) allowManualInput: boolean = true;

  /** Description message (use description slot as alternative) */
  @Prop() description: string;

  /** Long description message appearing in a popover (use description-long slot as alternative) */
  @Prop() descriptionLong: string;

  /** One or multiple error message (use error slot as alternative) */
  @Prop() error: string | string[];

  /** Invalid status */
  @Prop({ reflect: true }) invalid: boolean = false;

  /** Required status */
  @Prop({ reflect: true }) required: boolean = false;

  /** Size variant */
  @Prop({ reflect: true }) size: "default" | "small" = "default";

  /** Id of the element which describes the input */
  @Prop({ attribute: "a11y-describedby" }) a11yDescribedby: string = "";

  /**  Translation strings. */
  @Prop() localization: StzhPaginationLocalizedText;

  /** Pagination change event */
  @Event() stzhChange: EventEmitter<StzhPaginationChangeEvent>;

  @Element() element: HTMLStzhPaginationElement;

  /** Set current page to previous */
  @Method()
  async prev() {
    this.gotoPage(this.value - 1);
  }

  /** Set current page to next */
  @Method()
  async next() {
    this.gotoPage(this.value + 1);
  }

  private input: HTMLStzhInputElement;

  private gotoPage = (newPage: number) => {
    const previous = this.value;

    if (newPage >= 1 && newPage <= this.total) {
      this.value = newPage;
    } else if (newPage > this.total) {
      this.value = this.total;
    } else {
      this.value = 1;
    }

    if (previous !== this.value) {
      this.stzhChange.emit({
        component: "stzh-pagination",
        page: Number(this.value),
      });
    }
  };

  private onRootFocus = () => {
    if (this.input) {
      this.input.focus();
    }
  };

  private onInputChange = (event: CustomEvent<StzhInputChangeEvent>) => {
    const newPage = Number(event.detail.value) || 1;
    this.gotoPage(newPage);
  };

  async componentWillLoad() {
    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "pagination");
    }
  }

  render() {
    const classes = {
      "stzh-pagination": true,
      [`stzh-pagination--size-${this.size}`]: !!this.size,
    };

    return (
      <Host tabindex={this.disabled ? null : "-1"} onFocus={this.onRootFocus}>
        <div class={classes}>
          {this.allowManualInput ? (
            <Fragment>
              <stzh-input
                class="stzh-pagination__input"
                type="number"
                min={1}
                max={this.total}
                label={this.label || this.localization.label}
                labelHidden={true}
                ref={el => (this.input = el as HTMLStzhInputElement)}
                onStzhChange={this.onInputChange}
                value={this.value?.toString() || ""}
                required={this.required}
                disabled={this.disabled}
                description={this.description}
                descriptionLong={this.descriptionLong}
                error={this.error}
                a11yDescribedby={this.a11yDescribedby}
                name={this.name}
                size={this.size}
                invalid={this.invalid}
                hideOptional={true}
                noAutocomplete={true}
              ></stzh-input>
              <div class="stzh-pagination__total">
                {this.localization.totalLabel.replace(/\{total\}/gi, this.total.toString())}
              </div>
            </Fragment>
          ) : (
            <div class="stzh-pagination__total">
              {this.value} {this.localization.totalLabel.replace(/\{total\}/gi, this.total.toString())}
            </div>
          )}
          <stzh-buttongroup>
            <stzh-button
              variant="tertiary"
              icon="angle-left"
              icon-only
              size={this.size}
              a11yDisabled={this.value <= 1}
              a11yDescribedby={this.a11yDescribedby}
              a11yLabel={`${this.label || this.localization.label}, ${this.localization.prevLabel}, ${
                this.localization.selectedPageLabel
              } ${this.value}`}
              disabled={this.disabled}
              onClick={() => this.prev()}
            ></stzh-button>
            <stzh-button
              variant="tertiary"
              icon="angle-right"
              icon-only
              size={this.size}
              a11yDisabled={this.value >= this.total}
              a11yDescribedby={this.a11yDescribedby}
              a11yLabel={`${this.label || this.localization.label}, ${this.localization.nextLabel}, ${
                this.localization.selectedPageLabel
              } ${this.value}`}
              disabled={this.disabled}
              onClick={() => this.next()}
            ></stzh-button>
          </stzh-buttongroup>
        </div>
      </Host>
    );
  }
}
